/*
 * Encabezado del live portado (sections/skindion-live-header.liquid).
 * Reemplaza, sin dependencias, a lo que hacían en Getmore 5.3.0:
 * - theme.js: MenuDrawer / HeaderDrawer (menú móvil con <details>).
 * - js-layout.js: header-section (menús desplegables de escritorio y alturas en variables CSS).
 * El carrito no pasa por aquí: lo abre el drawer de Horizon (on:click="#cart-drawer/toggle").
 */
(() => {
  const DESKTOP = window.matchMedia('(min-width: 944px)');
  const FOCUSABLE =
    'summary, a[href], button:enabled, [tabindex]:not([tabindex^="-"]), input:not([type=hidden]):enabled, select:enabled, textarea:enabled';

  /** Menú móvil: <details> a pantalla completa (live: HeaderDrawer). */
  class SkindionLiveDrawer extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      this.summary = this.details?.querySelector('summary');
      this.panel = this.details?.querySelector('.sl-menu-drawer');
      if (!this.details || !this.summary || !this.panel) return;

      this.summary.setAttribute('role', 'button');
      this.summary.setAttribute('aria-expanded', 'false');

      this.onSummaryClick = this.onSummaryClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onClose = this.onClose.bind(this);
      this.onBreakpoint = this.onBreakpoint.bind(this);

      this.summary.addEventListener('click', this.onSummaryClick);
      this.addEventListener('keydown', this.onKeyDown);
      this.querySelectorAll('[data-sl-drawer-close]').forEach((button) => button.addEventListener('click', this.onClose));
      DESKTOP.addEventListener('change', this.onBreakpoint);
    }

    disconnectedCallback() {
      DESKTOP.removeEventListener('change', this.onBreakpoint);
    }

    get isOpen() {
      return this.details.hasAttribute('open');
    }

    onSummaryClick(event) {
      event.preventDefault();
      if (this.isOpen) this.close();
      else this.open();
    }

    onClose(event) {
      event.preventDefault();
      this.close(true);
    }

    onBreakpoint(media) {
      if (media.matches && this.isOpen) this.close(false, true);
    }

    onKeyDown(event) {
      if (!this.isOpen) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close(true);
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = Array.from(this.panel.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null);
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    open() {
      document.documentElement.style.setProperty('--sl-viewport-height', `${window.innerHeight}px`);
      this.details.setAttribute('open', '');
      this.summary.setAttribute('aria-expanded', 'true');

      // El bloqueo del scroll lo hace el CSS (body:has(.sl-menu-drawer-container[open])).
      window.requestAnimationFrame(() => {
        this.details.classList.add('menu-opening');
        const first = this.panel.querySelector(FOCUSABLE);
        (first || this.panel).focus({ preventScroll: true });
      });
    }

    /**
     * @param {boolean} [restoreFocus] - Devuelve el foco al botón del menú.
     * @param {boolean} [immediate] - Cierra sin esperar (cambio a escritorio).
     */
    close(restoreFocus = false, immediate = false) {
      this.details.classList.remove('menu-opening');
      this.summary.setAttribute('aria-expanded', 'false');

      // live: el panel se quita 200 ms después de quitar .menu-opening.
      const finish = () => {
        if (!this.details.classList.contains('menu-opening')) this.details.removeAttribute('open');
      };
      if (immediate) finish();
      else window.setTimeout(finish, 200);

      if (restoreFocus) this.summary.focus({ preventScroll: true });
    }
  }

  /** Barra: alturas para Horizon, menús desplegables de escritorio y selección de bloques en el editor. */
  class SkindionLiveHeader extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.shopify-section') || this;
      this.openClass = 'open-dropdown';

      this.setHeights = this.setHeights.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onBreakpoint = this.onBreakpoint.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onBlockDeselect = this.onBlockDeselect.bind(this);

      // ResizeObserver avisa antes del primer pintado y trae la altura sin forzar layout. Leer offsetHeight al conectar
      // costaba 180-220 ms con CPU 4x (L5).
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.onSectionResize.bind(this));
        this.resizeObserver.observe(this.section);
      } else {
        this.setHeights();
      }
      window.addEventListener('resize', this.setHeights, { passive: true });

      this.dropdowns = Array.from(this.querySelectorAll('.sl-desktop-nav-item.with-dropdown'));
      this.dropdowns.forEach((item) => {
        const trigger = item.querySelector('.sl-dropdown-trigger');
        trigger?.addEventListener('click', () => this.toggleDropdown(item));
      });
      if (this.dropdowns.length > 0) {
        document.addEventListener('click', this.onDocumentClick);
        DESKTOP.addEventListener('change', this.onBreakpoint);
      }

      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      window.removeEventListener('resize', this.setHeights);
      document.removeEventListener('click', this.onDocumentClick);
      DESKTOP.removeEventListener('change', this.onBreakpoint);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    /**
     * Horizon usa --header-height / --header-group-height (detalle de producto fijo, medios con altura
     * limitada, resumen del carrito). Sin su header nadie las mide: se escriben aquí, en <body>, igual
     * que utilities.js de Horizon.
     */
    onSectionResize(entries) {
      const box = entries[entries.length - 1]?.borderBoxSize?.[0];
      this.setHeights(box ? Math.round(box.blockSize) : undefined);
    }

    /** @param {number | Event} [known] - Altura ya medida; sin ella (o con el evento resize) se lee del DOM. */
    setHeights(known) {
      // Se lee todo antes de escribir: leer innerHeight con la variable ya escrita forzaba el layout de la página. Sin
      // cambios no se escribe: tocar estas variables recalcula los estilos de todo el documento.
      const height = typeof known === 'number' ? known : this.section.offsetHeight;
      const viewport = window.innerHeight;
      if (height !== this.lastHeight) {
        this.lastHeight = height;
        document.body.style.setProperty('--header-height', `${height}px`);
        document.body.style.setProperty('--header-group-height', `${height}px`);
      }
      if (viewport !== this.lastViewport) {
        this.lastViewport = viewport;
        document.documentElement.style.setProperty('--sl-viewport-height', `${viewport}px`);
      }
    }

    toggleDropdown(item) {
      const wasOpen = item.classList.contains(this.openClass);
      this.closeDropdowns();
      if (wasOpen) return;
      item.classList.add(this.openClass);
      item.querySelector('.sl-dropdown-trigger')?.setAttribute('aria-expanded', 'true');
    }

    closeDropdowns() {
      this.dropdowns.forEach((item) => {
        item.classList.remove(this.openClass);
        item.querySelector('.sl-dropdown-trigger')?.setAttribute('aria-expanded', 'false');
      });
    }

    onDocumentClick(event) {
      if (!DESKTOP.matches) return;
      const open = this.dropdowns.find((item) => item.classList.contains(this.openClass));
      if (open && !open.contains(/** @type {Node} */ (event.target))) this.closeDropdowns();
    }

    onBreakpoint() {
      this.closeDropdowns();
    }

    /** En el editor, al elegir un bloque del menú móvil se abre el menú para verlo. */
    onBlockSelect(event) {
      if (!this.contains(/** @type {Node} */ (event.target))) return;
      const drawer = this.querySelector('skindion-live-drawer');
      if (drawer && typeof drawer.open === 'function' && !drawer.isOpen) drawer.open();
    }

    onBlockDeselect(event) {
      if (!this.contains(/** @type {Node} */ (event.target))) return;
      const drawer = this.querySelector('skindion-live-drawer');
      if (drawer && typeof drawer.close === 'function' && drawer.isOpen) drawer.close();
    }
  }

  if (!customElements.get('skindion-live-drawer')) customElements.define('skindion-live-drawer', SkindionLiveDrawer);
  if (!customElements.get('skindion-live-header')) customElements.define('skindion-live-header', SkindionLiveHeader);
})();
